import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Utensils, Zap } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { IMAGES } from "@/assets";

const lineVariant = {
  hidden: { y: "110%" },
  show: (i) => ({ y: 0, transition: { duration: 0.9, delay: 0.15 + i * 0.1, ease: [0.16, 1, 0.3, 1] } }),
};

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);

  const lines = [t.hero.line1, t.hero.line2, t.hero.line3];

  return (
    <section id="top" ref={ref} className="relative min-h-[92vh] flex items-end overflow-hidden bg-ink" data-testid="hero-section">
      {/* Full-bleed background */}
      <motion.img
        src={IMAGES.heroDarkStore}
        alt="RISORICE store"
        style={{ y: imgY, scale: imgScale }}
        className="absolute inset-0 w-full h-full object-cover"
        data-testid="hero-image"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-transparent to-transparent" />

      <div className="relative mx-auto max-w-[1280px] w-full px-5 md:px-8 pb-16 md:pb-24 pt-28">
        <motion.span
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-md border border-white/20 px-4 py-2 text-[11px] md:text-xs font-semibold uppercase tracking-[0.16em] text-white"
          data-testid="hero-kicker"
        >
          <Zap size={14} className="text-tricolor-green fill-tricolor-green" />
          {t.hero.kicker}
        </motion.span>

        <h1 className="mt-6 font-display font-extrabold tracking-tight leading-[0.92] text-5xl md:text-7xl lg:text-[5.5rem] text-white max-w-4xl" data-testid="hero-title">
          {lines.map((line, i) => (
            <span key={i} className="block overflow-hidden">
              <motion.span className="block" custom={i} variants={lineVariant} initial="hidden" animate="show">
                {i === 0 ? line : <span className="text-tricolor-green">{line}</span>}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.55, duration: 0.8 }}
          className="mt-7 max-w-xl text-base md:text-xl text-white/80 leading-relaxed"
          data-testid="hero-lead"
        >
          {t.hero.lead}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7, duration: 0.8 }}
          className="mt-9 flex flex-wrap gap-3"
        >
          <a href="#menu" className="btn-primary !px-8 !py-4" data-testid="hero-cta-primary">
            <Utensils size={18} /> {t.hero.ctaPrimary}
          </a>
          <a href="#franchise" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 text-white text-sm font-semibold px-8 py-4 hover:bg-white hover:text-ink transition-colors duration-200" data-testid="hero-cta-secondary">
            {t.hero.ctaSecondary} <ArrowRight size={18} />
          </a>
        </motion.div>
      </div>
    </section>
  );
};
